import { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListInfraItems,
  useCreateInfraItem,
  useUpdateInfraItem,
  useDeleteInfraItem,
  useListInfraAcquisitions,
  useCreateInfraAcquisition,
  useDeleteInfraAcquisition,
  useListInfraWarranty,
  useListInventoryVendors,
  useListInfraCategories,
  useCreateInfraCategory,
  useUpdateInfraCategory,
  useDeleteInfraCategory,
  useListInfraItemVariants,
  useCreateInfraItemVariant,
  useUpdateInfraItemVariant,
  useDeleteInfraItemVariant,
  useListInfraAcquisitionPayments,
  useDeleteInfraAcquisitionPayment,
  getListInfraItemsQueryKey,
  getListInfraAcquisitionsQueryKey,
  getListInfraWarrantyQueryKey,
  getListInfraCategoriesQueryKey,
  getListInfraItemVariantsQueryKey,
  getListInfraAcquisitionPaymentsQueryKey,
} from "@workspace/api-client-react";
import type {
  InfraItem,
  InfraItemVariant,
  InfraAcquisition,
  InfraCategory,
  CreateInfraItemBody,
  CreateInfraAcquisitionBody,
  InfraItemPricingModel,
  InfraItemLabourBasis,
  InfraAcquisitionLabourBasis,
  ListInfraWarrantyView,
  ListInfraAcquisitionsPaymentStatus,
} from "@workspace/api-client-react";
import { InfraCreateVariantRow } from "@/components/inventory/infra-create-variant-row";
import type { InfraVariantRowValue } from "@/components/inventory/infra-create-variant-row";
import { InfraVariantDialog } from "@/components/inventory/infra-variant-dialog";
import { InfraAddPaymentDialog } from "@/components/inventory/infra-add-payment-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { uploadPaymentProof, uploadInvoice, uploadItemPhoto, type UploadResult } from "@/lib/upload";
import { Building2, PackagePlus, ShieldCheck, Loader2, Upload, X, Pencil, Trash2 } from "lucide-react";

// ── Types ──────────────────────────────────────────────────────────────────────

type TabValue = "catalog" | "acquisitions" | "warranty" | "categories";

const TAB_VALUES: readonly TabValue[] = ["catalog", "acquisitions", "warranty", "categories"];

function tabFromPath(path: string): TabValue {
  // Match /inventory/infra/<tab>; fall back to "acquisitions" on bare /inventory/infra.
  const suffix = path.replace(/^\/inventory\/infra\/?/, "").split("/")[0] ?? "";
  return (TAB_VALUES as readonly string[]).includes(suffix) ? (suffix as TabValue) : "acquisitions";
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const inr = (n: number | string | null | undefined) =>
  `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const today = () => new Date().toISOString().slice(0, 10);

const GST_RATES = [0, 5, 12, 18, 28] as const;
const PAYMENT_METHODS = ["Cash", "UPI", "Bank_Transfer", "Card", "Cheque"] as const;
const PAID_BY = ["Company", "Personal"] as const;

function pricingModelLabel(m: InfraItemPricingModel | string) {
  if (m === "Unit") return "Unit";
  if (m === "Measurement") return "Measurement";
  if (m === "Setup_Work") return "Setup / Work";
  return m;
}

function labourBasisLabel(b: InfraItemLabourBasis | InfraAcquisitionLabourBasis | null | undefined) {
  if (b === "Per_Day") return "Per Day";
  if (b === "Per_Area") return "Per Area";
  if (b === "Lump_Sum") return "Lump Sum";
  return b ?? "—";
}

// Mirrors server computeTotalCost
function computeLiveTotal(form: AcquisitionForm): number {
  const pm = form.pricingModel;
  if (pm === "Unit") {
    return Number(form.qty || 0) * Number(form.unitPrice || 0);
  }
  if (pm === "Measurement") {
    return Number(form.measurement || 0) * Number(form.ratePerUnit || 0);
  }
  if (pm === "Setup_Work") {
    const material = Number(form.materialCost || 0);
    const basis = form.labourBasis;
    if (basis === "Per_Day") return material + Number(form.labourDays || 0) * Number(form.labourRate || 0);
    if (basis === "Per_Area") return material + Number(form.labourArea || 0) * Number(form.labourRate || 0);
    if (basis === "Lump_Sum") return material + Number(form.labourLumpSum || 0);
  }
  return 0;
}

// ── FileField (copied from expenses.tsx) ─────────────────────────────────────

function FileField({
  value, onChange, uploadFn, label,
}: {
  value: string | null;
  onChange: (url: string | null) => void;
  uploadFn: (f: File) => Promise<UploadResult>;
  label: string;
}) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);

  async function handle(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const r = await uploadFn(f);
      if (r.ok) { onChange(r.url); toast({ title: `${label} uploaded.` }); }
      else toast({ title: r.message, variant: "destructive" });
    } finally { setUploading(false); e.target.value = ""; }
  }

  if (value) {
    return (
      <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-sm underline truncate flex-1">
          View {label.toLowerCase()}
        </a>
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }
  return (
    <label
      className={`flex cursor-pointer items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm hover:bg-muted/50 ${uploading ? "pointer-events-none opacity-60" : ""}`}
    >
      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
      <span>{uploading ? "Uploading…" : `Upload ${label.toLowerCase()}`}</span>
      <input
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
        className="sr-only"
        onChange={handle}
        disabled={uploading}
      />
    </label>
  );
}

// ── Catalog Tab ───────────────────────────────────────────────────────────────

interface CatalogForm {
  name: string;
  categoryId: string; // selected infra_categories.id (as string for the <Select>)
  pricingModel: "Unit" | "Measurement" | "Setup_Work";
  unitLabel: string;
  unitPrice: string;
  measurementUnit: string;
  ratePerUnit: string;
  labourBasis: "Per_Day" | "Per_Area" | "Lump_Sum" | "";
  labourRate: string;
  warrantyApplicable: boolean;
  warrantyMonths: string;
  hsnCode: string;
  gstRate: string;
  defaultVendorId: string;
  location: string;
  notes: string;
  imageUrl: string | null;
  status: "Active" | "Archived";
}

const EMPTY_CATALOG: CatalogForm = {
  name: "", categoryId: "", pricingModel: "Unit",
  unitLabel: "", unitPrice: "",
  measurementUnit: "", ratePerUnit: "",
  labourBasis: "", labourRate: "",
  warrantyApplicable: false, warrantyMonths: "",
  hsnCode: "", gstRate: "0",
  defaultVendorId: "", location: "", notes: "",
  imageUrl: null,
  status: "Active",
};

function catalogFromItem(item: InfraItem): CatalogForm {
  return {
    name: item.name,
    categoryId: item.categoryId != null ? String(item.categoryId) : "",
    pricingModel: item.pricingModel as "Unit" | "Measurement" | "Setup_Work",
    unitLabel: item.unitLabel ?? "",
    unitPrice: item.unitPrice != null ? String(item.unitPrice) : "",
    measurementUnit: item.measurementUnit ?? "",
    ratePerUnit: item.ratePerUnit != null ? String(item.ratePerUnit) : "",
    labourBasis: (item.labourBasis as "Per_Day" | "Per_Area" | "Lump_Sum" | "") ?? "",
    labourRate: item.labourRate != null ? String(item.labourRate) : "",
    warrantyApplicable: item.warrantyApplicable,
    warrantyMonths: item.warrantyMonths != null ? String(item.warrantyMonths) : "",
    hsnCode: item.hsnCode ?? "",
    gstRate: item.gstRate != null ? String(item.gstRate) : "0",
    defaultVendorId: item.defaultVendorId != null ? String(item.defaultVendorId) : "",
    location: item.location ?? "",
    notes: item.notes ?? "",
    imageUrl: item.imageUrl ?? null,
    status: item.status as "Active" | "Archived",
  };
}

// ── Variants Subtable for edit mode ──────────────────────────────────────────

function InfraVariantsSubtable({ item }: { item: InfraItem }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: variants = [] } = useListInfraItemVariants(item.id);
  const deleteMut = useDeleteInfraItemVariant();

  const [editVariant, setEditVariant] = useState<InfraItemVariant | null>(null);
  const [deleteVariant, setDeleteVariant] = useState<InfraItemVariant | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  async function confirmDeleteVariant() {
    if (!deleteVariant) return;
    try {
      await deleteMut.mutateAsync({ itemId: item.id, variantId: deleteVariant.id });
      toast({ title: "Variant deleted." });
      qc.invalidateQueries({ queryKey: getListInfraItemVariantsQueryKey(item.id) });
    } catch (err: unknown) {
      const s = (err as { status?: number })?.status;
      toast({
        title: s === 409
          ? "Variant is used by one or more acquisitions — cannot delete."
          : "Failed to delete variant.",
        variant: "destructive",
      });
    }
    setDeleteVariant(null);
  }

  return (
    <>
      <div className="mb-2 flex items-center justify-end">
        <Button type="button" size="sm" variant="outline" onClick={() => setAddOpen(true)}>
          + Add Variant
        </Button>
      </div>

      {variants.length === 0 ? (
        <p className="text-xs text-muted-foreground rounded-md border border-dashed px-3 py-2">
          No variants yet. Click &ldquo;+ Add Variant&rdquo; to create one.
        </p>
      ) : (
        <div className="rounded-md border divide-y">
          {variants.map((v) => (
            <div key={v.id} className="flex items-center justify-between px-3 py-2 text-sm">
              <div className="flex flex-col gap-0.5">
                <span className="font-medium">{v.displayName ?? `Variant #${v.id}`}</span>
                <span className="text-xs text-muted-foreground">
                  HSN: {v.hsnCode ?? "(parent)"} · GST: {v.gstRate != null ? `${v.gstRate}%` : "(parent)"} · Price: {v.unitPrice != null ? `₹${Number(v.unitPrice).toLocaleString("en-IN")}` : "(parent)"}
                </span>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button type="button" size="sm" variant="ghost" onClick={() => setEditVariant(v)}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button type="button" size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setDeleteVariant(v)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add variant dialog (create mode) */}
      {addOpen && (
        <InfraVariantDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          itemId={item.id}
          parentItem={item}
        />
      )}

      {/* Edit variant dialog */}
      {editVariant && (
        <InfraVariantDialog
          open={editVariant !== null}
          onOpenChange={(o) => { if (!o) setEditVariant(null); }}
          itemId={item.id}
          parentItem={item}
          variant={editVariant}
        />
      )}

      {/* Delete variant confirmation */}
      <AlertDialog open={deleteVariant !== null} onOpenChange={(o) => { if (!o) setDeleteVariant(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete variant &ldquo;{deleteVariant?.displayName ?? `#${deleteVariant?.id}`}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone. Variants used by acquisitions cannot be deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteVariant} disabled={deleteMut.isPending}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ── Catalog Tab ────────────────────────────────────────────────────────────────

interface CatalogFormWithVariants extends CatalogForm {
  variants: InfraVariantRowValue[];
}

function InfraCatalogTab({
  openCreate, setOpenCreate,
}: {
  openCreate: boolean;
  setOpenCreate: (v: boolean) => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: items = [], isLoading } = useListInfraItems();
  const { data: vendors = [] } = useListInventoryVendors();
  const { data: categories = [] } = useListInfraCategories({ status: "Active" });
  const createMut = useCreateInfraItem();
  const updateMut = useUpdateInfraItem();
  const deleteMut = useDeleteInfraItem();
  const createVariantMut = useCreateInfraItemVariant();

  const [editItem, setEditItem] = useState<InfraItem | null>(null);
  const [archiveItem, setArchiveItem] = useState<InfraItem | null>(null);
  const [form, setForm] = useState<CatalogForm>(EMPTY_CATALOG);

  // react-hook-form for inline variant rows (create mode)
  const rhf = useForm<CatalogFormWithVariants>({
    defaultValues: { ...EMPTY_CATALOG, variants: [] },
  });
  const variantsArray = useFieldArray({ control: rhf.control, name: "variants" });

  const isOpen = openCreate || editItem !== null;
  const isEditMode = editItem !== null;

  function openEdit(item: InfraItem) {
    setForm(catalogFromItem(item));
    setEditItem(item);
    rhf.reset({ ...catalogFromItem(item), variants: [] });
  }

  function openNew() {
    setForm(EMPTY_CATALOG);
    rhf.reset({ ...EMPTY_CATALOG, variants: [] });
    setOpenCreate(true);
  }

  function closeDialog() {
    setEditItem(null);
    setOpenCreate(false);
    rhf.reset({ ...EMPTY_CATALOG, variants: [] });
  }

  function set<K extends keyof CatalogForm>(key: K, value: CatalogForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit() {
    const catId = form.categoryId ? Number(form.categoryId) : null;
    const catName = catId != null ? (categories.find((c) => c.id === catId)?.name ?? null) : null;
    const body: CreateInfraItemBody = {
      name: form.name,
      categoryId: catId,
      category: catName, // keep the denormalized name in sync for display
      pricingModel: form.pricingModel,
      unitLabel: form.pricingModel === "Unit" ? form.unitLabel || null : null,
      unitPrice: form.pricingModel === "Unit" && form.unitPrice ? Number(form.unitPrice) : null,
      measurementUnit: form.pricingModel === "Measurement" ? form.measurementUnit || null : null,
      ratePerUnit: form.pricingModel === "Measurement" && form.ratePerUnit ? Number(form.ratePerUnit) : null,
      labourBasis: form.pricingModel === "Setup_Work" && form.labourBasis ? form.labourBasis : null,
      labourRate: form.pricingModel === "Setup_Work" && form.labourRate ? Number(form.labourRate) : null,
      warrantyApplicable: form.warrantyApplicable,
      warrantyMonths: form.warrantyApplicable && form.warrantyMonths ? Number(form.warrantyMonths) : null,
      hsnCode: form.hsnCode || null,
      gstRate: form.gstRate ? Number(form.gstRate) : null,
      defaultVendorId: form.defaultVendorId ? Number(form.defaultVendorId) : null,
      location: form.location || null,
      notes: form.notes || null,
      imageUrl: form.imageUrl,
      status: form.status,
    };

    try {
      if (editItem) {
        await updateMut.mutateAsync({ id: editItem.id, data: body });
        toast({ title: "Item updated." });
      } else {
        const created = await createMut.mutateAsync({ data: body });
        // Submit pending inline variant rows after item is created
        const pendingVariants = rhf.getValues("variants");
        if (pendingVariants && pendingVariants.length > 0) {
          const createdItemId = (created as { id?: number })?.id;
          if (createdItemId) {
            for (const row of pendingVariants) {
              if (!row.attributeValueId) continue; // skip incomplete rows
              try {
                await createVariantMut.mutateAsync({
                  itemId: createdItemId,
                  data: {
                    attributeValueIds: [row.attributeValueId],
                    itemCode: row.itemCode ?? null,
                    hsnCode: row.hsnCode ?? null,
                    gstRate: row.gstRate ?? null,
                    unitPrice: row.unitPrice ?? null,
                    status: row.status ?? "Active",
                  },
                });
              } catch {
                // Non-fatal — item was created; variant creation failed
                toast({ title: "Item created but a variant could not be saved.", variant: "destructive" });
              }
            }
          }
        }
        toast({ title: "Item created." });
      }
      await qc.invalidateQueries({ queryKey: getListInfraItemsQueryKey() });
      closeDialog();
    } catch (err: unknown) {
      const msg = (err as { response?: { status?: number } })?.response?.status === 409
        ? "An item with that name already exists."
        : "Failed to save item.";
      toast({ title: msg, variant: "destructive" });
    }
  }

  async function handleArchive(item: InfraItem) {
    try {
      await updateMut.mutateAsync({ id: item.id, data: { status: "Archived" } });
      toast({ title: "Item archived." });
      await qc.invalidateQueries({ queryKey: getListInfraItemsQueryKey() });
    } catch {
      toast({ title: "Failed to archive.", variant: "destructive" });
    }
    setArchiveItem(null);
  }

  const isBusy = createMut.isPending || updateMut.isPending;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={openNew}>
          <PackagePlus className="mr-2 h-4 w-4" />Add Item
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Pricing Model</TableHead>
                <TableHead>Rate</TableHead>
                <TableHead>Warranty</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No infra items yet.
                  </TableCell>
                </TableRow>
              ) : items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {item.imageUrl && (
                        <img
                          src={item.imageUrl}
                          alt=""
                          className="h-8 w-8 shrink-0 rounded border object-cover"
                        />
                      )}
                      <span>{item.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>{item.category ?? "—"}</TableCell>
                  <TableCell>{pricingModelLabel(item.pricingModel)}</TableCell>
                  <TableCell>
                    {item.pricingModel === "Unit" && item.unitPrice != null && (
                      <>{inr(item.unitPrice)}{item.unitLabel ? ` / ${item.unitLabel}` : ""}</>
                    )}
                    {item.pricingModel === "Measurement" && item.ratePerUnit != null && (
                      <>{inr(item.ratePerUnit)}{item.measurementUnit ? ` / ${item.measurementUnit}` : ""}</>
                    )}
                    {item.pricingModel === "Setup_Work" && item.labourRate != null && (
                      <>{inr(item.labourRate)} labour ({labourBasisLabel(item.labourBasis)})</>
                    )}
                    {item.pricingModel === "Setup_Work" && item.labourRate == null && "—"}
                  </TableCell>
                  <TableCell>
                    {item.warrantyApplicable ? (
                      <Badge variant="secondary">{item.warrantyMonths ?? "?"} mo</Badge>
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={item.status === "Active" ? "default" : "outline"}>
                      {item.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(item)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {item.status === "Active" && (
                        <Button variant="ghost" size="sm" onClick={() => setArchiveItem(item)}>
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={isOpen} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editItem ? "Edit Infra Item" : "Add Infra Item"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            {/* Name */}
            <div className="grid gap-1.5">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Office Chair" />
            </div>
            {/* Category — managed list (Inventory → Infra → Categories) */}
            <div className="grid gap-1.5">
              <Label>Category</Label>
              <Select value={form.categoryId || "none"} onValueChange={(v) => set("categoryId", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name} · {c.billingType === "Client_Billable" ? `Client${c.paymentType ? ` (${c.paymentType.replace(/_/g, " ")})` : ""}` : "Office"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Manage categories in the Categories tab.</p>
            </div>
            {/* Pricing Model */}
            <div className="grid gap-1.5">
              <Label>Pricing Model *</Label>
              <Select value={form.pricingModel} onValueChange={(v) => set("pricingModel", v as CatalogForm["pricingModel"])}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Unit">Unit</SelectItem>
                  <SelectItem value="Measurement">Measurement</SelectItem>
                  <SelectItem value="Setup_Work">Setup / Work</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Unit fields */}
            {form.pricingModel === "Unit" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-1.5">
                  <Label>Unit Label</Label>
                  <Input value={form.unitLabel} onChange={(e) => set("unitLabel", e.target.value)} placeholder="e.g. piece" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Unit Price (₹)</Label>
                  <Input type="number" min="0" value={form.unitPrice} onChange={(e) => set("unitPrice", e.target.value)} placeholder="4500" />
                </div>
              </div>
            )}

            {/* Measurement fields */}
            {form.pricingModel === "Measurement" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-1.5">
                  <Label>Measurement Unit</Label>
                  <Input value={form.measurementUnit} onChange={(e) => set("measurementUnit", e.target.value)} placeholder="e.g. sq ft" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Rate per Unit (₹)</Label>
                  <Input type="number" min="0" value={form.ratePerUnit} onChange={(e) => set("ratePerUnit", e.target.value)} placeholder="350" />
                </div>
              </div>
            )}

            {/* Setup / Work fields */}
            {form.pricingModel === "Setup_Work" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-1.5">
                  <Label>Labour Basis</Label>
                  <Select value={form.labourBasis || ""} onValueChange={(v) => set("labourBasis", v as CatalogForm["labourBasis"])}>
                    <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Per_Day">Per Day</SelectItem>
                      <SelectItem value="Per_Area">Per Area</SelectItem>
                      <SelectItem value="Lump_Sum">Lump Sum</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label>Labour Rate (₹)</Label>
                  <Input type="number" min="0" value={form.labourRate} onChange={(e) => set("labourRate", e.target.value)} placeholder="0" />
                </div>
              </div>
            )}

            {/* Warranty */}
            <div className="flex items-center gap-3">
              <Switch
                id="warranty-applicable"
                checked={form.warrantyApplicable}
                onCheckedChange={(v) => set("warrantyApplicable", v)}
              />
              <Label htmlFor="warranty-applicable">Warranty Applicable</Label>
              {form.warrantyApplicable && (
                <Input
                  type="number"
                  min="1"
                  className="w-32"
                  value={form.warrantyMonths}
                  onChange={(e) => set("warrantyMonths", e.target.value)}
                  placeholder="Months"
                />
              )}
            </div>

            {/* HSN + GST */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label>HSN Code</Label>
                <Input value={form.hsnCode} onChange={(e) => set("hsnCode", e.target.value)} placeholder="e.g. 9401" />
              </div>
              <div className="grid gap-1.5">
                <Label>GST Rate (%)</Label>
                <Select value={String(form.gstRate)} onValueChange={(v) => set("gstRate", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {GST_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Vendor */}
            <div className="grid gap-1.5">
              <Label>Default Vendor</Label>
              <Select value={form.defaultVendorId || "__none__"} onValueChange={(v) => set("defaultVendorId", v === "__none__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {vendors.map((v) => <SelectItem key={v.id} value={String(v.id)}>{v.vendorName}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Location */}
            <div className="grid gap-1.5">
              <Label>Location</Label>
              <Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g. Ground floor reception" />
            </div>

            {/* Notes */}
            <div className="grid gap-1.5">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
            </div>

            {/* Image */}
            <div className="grid gap-1.5">
              <Label>Image</Label>
              {form.imageUrl && (
                <img
                  src={form.imageUrl}
                  alt={form.name || "Catalogue item"}
                  className="h-24 w-24 rounded-md border object-cover"
                />
              )}
              <FileField
                value={form.imageUrl}
                onChange={(v) => set("imageUrl", v)}
                uploadFn={uploadItemPhoto}
                label="Image"
              />
            </div>

            {/* Status */}
            {editItem && (
              <div className="grid gap-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v as "Active" | "Archived")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* ── Variants section ── */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">Variants</p>
                {!isEditMode && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      variantsArray.append({
                        attributeKeyId: null,
                        attributeValueId: null,
                        status: "Active",
                      })
                    }
                  >
                    + Add Variant
                  </Button>
                )}
              </div>

              {/* Create mode: inline field-array rows */}
              {!isEditMode && (
                <>
                  {variantsArray.fields.length === 0 && (
                    <p className="text-xs text-muted-foreground rounded-md border border-dashed px-3 py-2">
                      Optional — leave empty to save as a single-SKU item.
                    </p>
                  )}
                  <div className="space-y-2">
                    {variantsArray.fields.map((field, i) => (
                      <InfraCreateVariantRow
                        key={field.id}
                        index={i}
                        control={rhf.control}
                        setValue={rhf.setValue}
                        onRemove={() => variantsArray.remove(i)}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* Edit mode: variants subtable (add/edit/delete inline) */}
              {isEditMode && editItem && (
                <InfraVariantsSubtable item={editItem} />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={isBusy}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={isBusy || !form.name.trim()}>
              {isBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editItem ? "Save Changes" : "Create Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive confirmation */}
      <AlertDialog open={!!archiveItem} onOpenChange={(o) => { if (!o) setArchiveItem(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive "{archiveItem?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>The item will be hidden from new acquisitions but existing records are preserved.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => archiveItem && handleArchive(archiveItem)}>Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Acquisitions Tab ──────────────────────────────────────────────────────────

interface AcquisitionForm {
  infraItemId: string;
  variantId: string; // selected variant id (empty = no variant)
  vendorId: string;
  acquisitionDate: string;
  pricingModel: "Unit" | "Measurement" | "Setup_Work";
  // Unit
  qty: string;
  unitPrice: string;
  // Measurement
  measurement: string;
  ratePerUnit: string;
  // Setup_Work
  materialCost: string;
  labourBasis: "Per_Day" | "Per_Area" | "Lump_Sum" | "";
  labourDays: string;
  labourArea: string;
  labourRate: string;
  labourLumpSum: string;
  // Common
  gstApplicable: boolean; // Phase 35: GST-applicable toggle
  gstRate: string;
  warrantyMonths: string;
  warrantyStartDate: string;
  location: string;
  // Payment
  paidBy: string;
  paymentMethod: string;
  paymentAccountDetail: string;
  paymentReference: string;
  paymentDate: string;
  paymentStatus: "Paid" | "Pending";
  invoiceUrl: string | null;
  paymentProofUrl: string | null;
  notes: string;
}

const EMPTY_ACQUISITION: AcquisitionForm = {
  infraItemId: "", variantId: "", vendorId: "",
  acquisitionDate: today(),
  pricingModel: "Unit",
  qty: "", unitPrice: "",
  measurement: "", ratePerUnit: "",
  materialCost: "", labourBasis: "", labourDays: "", labourArea: "", labourRate: "", labourLumpSum: "",
  gstApplicable: true,
  gstRate: "0",
  warrantyMonths: "", warrantyStartDate: "",
  location: "",
  paidBy: "", paymentMethod: "", paymentAccountDetail: "", paymentReference: "", paymentDate: today(),
  paymentStatus: "Pending",
  invoiceUrl: null, paymentProofUrl: null,
  notes: "",
};

// ── Payments section for a single acquisition ─────────────────────────────────

function InfraAcquisitionPaymentsSection({ acq }: { acq: InfraAcquisition }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const { data: payments = [] } = useListInfraAcquisitionPayments(acq.id);
  const deleteMut = useDeleteInfraAcquisitionPayment();

  const paidSum = payments.reduce((s, p) => s + Number(p.amount), 0);
  const totalCost = Number(acq.totalCost ?? 0);
  const balance = Math.max(0, totalCost - paidSum);

  const inr = (n: number) =>
    `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  async function handleDeletePayment(paymentId: number) {
    try {
      await deleteMut.mutateAsync({ acquisitionId: acq.id, paymentId });
      qc.invalidateQueries({ queryKey: getListInfraAcquisitionPaymentsQueryKey(acq.id) });
      qc.invalidateQueries({ queryKey: getListInfraAcquisitionsQueryKey() });
      toast({ title: "Payment deleted." });
    } catch {
      toast({ title: "Failed to delete payment.", variant: "destructive" });
    }
  }

  return (
    <div className="mt-2 ml-4 space-y-2">
      <div className="flex items-center gap-4 text-sm">
        <span className="text-muted-foreground">
          Paid <span className="font-semibold text-foreground">{inr(paidSum)}</span>
        </span>
        <span className="text-muted-foreground">
          Balance <span className={`font-semibold ${balance > 0 ? "text-amber-700" : "text-green-700"}`}>{inr(balance)}</span>
        </span>
        <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
          + Add Payment
        </Button>
      </div>

      {payments.length > 0 && (
        <table className="w-full text-xs border rounded-md">
          <thead>
            <tr className="bg-muted/30">
              <th className="text-left px-2 py-1">Date</th>
              <th className="text-left px-2 py-1">Amount</th>
              <th className="text-left px-2 py-1">Method</th>
              <th className="text-left px-2 py-1">Paid By</th>
              <th className="text-left px-2 py-1">Reference</th>
              <th className="px-2 py-1 w-8" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {payments.map((p) => (
              <tr key={p.id}>
                <td className="px-2 py-1">{String(p.paymentDate).slice(0, 10)}</td>
                <td className="px-2 py-1 font-medium">{inr(Number(p.amount))}</td>
                <td className="px-2 py-1">{p.paymentMethod ?? "—"}</td>
                <td className="px-2 py-1">{p.paidBy ?? "—"}</td>
                <td className="px-2 py-1">{p.paymentReference ?? "—"}</td>
                <td className="px-2 py-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => handleDeletePayment(p.id)}
                    disabled={deleteMut.isPending}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <InfraAddPaymentDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        acquisitionId={acq.id}
        totalCost={totalCost}
        paidSoFar={paidSum}
      />
    </div>
  );
}

// ── Acquisitions Tab ───────────────────────────────────────────────────────────

function InfraAcquisitionsTab({
  openCreate, setOpenCreate,
}: {
  openCreate: boolean;
  setOpenCreate: (v: boolean) => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const [filterItemId, setFilterItemId] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("__all__");
  const [expandedPayments, setExpandedPayments] = useState<Set<number>>(new Set());

  const params: { itemId?: number; paymentStatus?: ListInfraAcquisitionsPaymentStatus } = {};
  if (filterItemId) params.itemId = Number(filterItemId);
  if (filterStatus !== "__all__") params.paymentStatus = filterStatus as ListInfraAcquisitionsPaymentStatus;

  const { data: acquisitions = [], isLoading } = useListInfraAcquisitions(params);
  const { data: items = [] } = useListInfraItems();
  const { data: vendors = [] } = useListInventoryVendors();
  const createMut = useCreateInfraAcquisition();
  const deleteMut = useDeleteInfraAcquisition();

  const [form, setForm] = useState<AcquisitionForm>(EMPTY_ACQUISITION);
  const [deleteAcq, setDeleteAcq] = useState<InfraAcquisition | null>(null);

  // Variant data for the selected item in the create dialog
  const selectedItemId = form.infraItemId ? Number(form.infraItemId) : 0;
  const { data: itemVariants = [] } = useListInfraItemVariants(
    selectedItemId,
    undefined,
    {
      query: {
        queryKey: getListInfraItemVariantsQueryKey(selectedItemId),
        enabled: selectedItemId > 0,
      },
    },
  );

  const liveTotal = computeLiveTotal(form);
  const isBusy = createMut.isPending;

  function setF<K extends keyof AcquisitionForm>(key: K, value: AcquisitionForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function handleItemSelect(itemId: string) {
    const item = items.find((i) => String(i.id) === itemId);
    setF("infraItemId", itemId);
    setF("variantId", ""); // clear variant when item changes
    if (item) {
      setF("pricingModel", item.pricingModel as "Unit" | "Measurement" | "Setup_Work");
      setF("unitPrice", item.unitPrice != null ? String(item.unitPrice) : "");
      setF("ratePerUnit", item.ratePerUnit != null ? String(item.ratePerUnit) : "");
      setF("labourBasis", (item.labourBasis as "Per_Day" | "Per_Area" | "Lump_Sum" | "") ?? "");
      setF("labourRate", item.labourRate != null ? String(item.labourRate) : "");
      setF("gstRate", item.gstRate != null ? String(item.gstRate) : "0");
      setF("warrantyMonths", item.warrantyApplicable && item.warrantyMonths ? String(item.warrantyMonths) : "");
      setF("vendorId", item.defaultVendorId != null ? String(item.defaultVendorId) : "");
      setF("location", item.location ?? "");
    }
  }

  function handleVariantSelect(variantId: string) {
    setF("variantId", variantId);
    if (!variantId) return;
    const variant = itemVariants.find((v) => String(v.id) === variantId);
    const parentItem = items.find((i) => String(i.id) === form.infraItemId);
    if (variant && parentItem) {
      // Auto-fill: variant override ?? parent fallback
      const effectiveGstRate = variant.gstRate ?? parentItem.gstRate;
      const effectiveUnitPrice = variant.unitPrice ?? parentItem.unitPrice;
      if (effectiveGstRate != null) setF("gstRate", String(effectiveGstRate));
      if (effectiveUnitPrice != null) setF("unitPrice", String(effectiveUnitPrice));
    }
  }

  function closeDialog() {
    setOpenCreate(false);
    setForm(EMPTY_ACQUISITION);
  }

  function togglePaymentRow(acqId: number) {
    setExpandedPayments((prev) => {
      const next = new Set(prev);
      if (next.has(acqId)) next.delete(acqId);
      else next.add(acqId);
      return next;
    });
  }

  async function handleSubmit() {
    const body: CreateInfraAcquisitionBody = {
      infraItemId: Number(form.infraItemId),
      variantId: form.variantId ? Number(form.variantId) : null,
      gstApplicable: form.gstApplicable,
      vendorId: form.vendorId ? Number(form.vendorId) : null,
      acquisitionDate: form.acquisitionDate,
      pricingModel: form.pricingModel,
      qty: form.pricingModel === "Unit" && form.qty ? Number(form.qty) : null,
      unitPrice: form.pricingModel === "Unit" && form.unitPrice ? Number(form.unitPrice) : null,
      measurement: form.pricingModel === "Measurement" && form.measurement ? Number(form.measurement) : null,
      ratePerUnit: form.pricingModel === "Measurement" && form.ratePerUnit ? Number(form.ratePerUnit) : null,
      materialCost: form.pricingModel === "Setup_Work" && form.materialCost ? Number(form.materialCost) : null,
      labourBasis: form.pricingModel === "Setup_Work" && form.labourBasis ? form.labourBasis : null,
      labourDays: form.pricingModel === "Setup_Work" && form.labourBasis === "Per_Day" && form.labourDays ? Number(form.labourDays) : null,
      labourArea: form.pricingModel === "Setup_Work" && form.labourBasis === "Per_Area" && form.labourArea ? Number(form.labourArea) : null,
      labourRate: form.pricingModel === "Setup_Work" && (form.labourBasis === "Per_Day" || form.labourBasis === "Per_Area") && form.labourRate ? Number(form.labourRate) : null,
      labourLumpSum: form.pricingModel === "Setup_Work" && form.labourBasis === "Lump_Sum" && form.labourLumpSum ? Number(form.labourLumpSum) : null,
      gstRate: form.gstApplicable && form.gstRate ? Number(form.gstRate) : null,
      warrantyMonths: form.warrantyMonths ? Number(form.warrantyMonths) : null,
      warrantyStartDate: form.warrantyStartDate || null,
      location: form.location || null,
      paidBy: form.paidBy || null,
      paymentMethod: form.paymentMethod || null,
      paymentAccountDetail: form.paymentAccountDetail || null,
      paymentReference: form.paymentReference || null,
      paymentDate: form.paymentDate || null,
      paymentProofUrl: form.paymentProofUrl || null,
      invoiceUrl: form.invoiceUrl || null,
      paymentStatus: form.paymentStatus,
      notes: form.notes || null,
    };

    try {
      await createMut.mutateAsync({ data: body });
      toast({ title: "Acquisition logged." });
      await qc.invalidateQueries({ queryKey: getListInfraAcquisitionsQueryKey() });
      await qc.invalidateQueries({ queryKey: getListInfraWarrantyQueryKey() });
      closeDialog();
    } catch {
      toast({ title: "Failed to save acquisition.", variant: "destructive" });
    }
  }

  async function handleDelete(acq: InfraAcquisition) {
    try {
      await deleteMut.mutateAsync({ id: acq.id });
      toast({ title: "Acquisition deleted." });
      await qc.invalidateQueries({ queryKey: getListInfraAcquisitionsQueryKey() });
      await qc.invalidateQueries({ queryKey: getListInfraWarrantyQueryKey() });
    } catch {
      toast({ title: "Failed to delete.", variant: "destructive" });
    }
    setDeleteAcq(null);
  }

  const itemMap = new Map(items.map((i) => [i.id, i]));
  const vendorMap = new Map(vendors.map((v) => [v.id, v]));

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={filterItemId || "__all__"} onValueChange={(v) => setFilterItemId(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-52"><SelectValue placeholder="All items" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All items</SelectItem>
            {items.map((i) => <SelectItem key={i.id} value={String(i.id)}>{i.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-44"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            <SelectItem value="Paid">Paid</SelectItem>
            <SelectItem value="Partially_Paid">Partially Paid</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button size="sm" onClick={() => { setForm(EMPTY_ACQUISITION); setOpenCreate(true); }}>
          <PackagePlus className="mr-2 h-4 w-4" />Log Acquisition
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Warranty End</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {acquisitions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No acquisitions found.
                  </TableCell>
                </TableRow>
              ) : acquisitions.map((acq) => (
                <>
                  <TableRow key={acq.id}>
                    <TableCell>{String(acq.acquisitionDate).slice(0, 10)}</TableCell>
                    <TableCell className="font-medium">{itemMap.get(acq.infraItemId)?.name ?? `#${acq.infraItemId}`}</TableCell>
                    <TableCell>{acq.vendorId ? (vendorMap.get(acq.vendorId)?.vendorName ?? `#${acq.vendorId}`) : "—"}</TableCell>
                    <TableCell>{pricingModelLabel(acq.pricingModel)}</TableCell>
                    <TableCell>{inr(acq.totalCost)}</TableCell>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => togglePaymentRow(acq.id)}
                        className="focus:outline-none"
                        title="Show/hide payments"
                      >
                        <Badge
                          variant={
                            acq.paymentStatus === "Paid"
                              ? "default"
                              : acq.paymentStatus === "Partially_Paid"
                                ? "secondary"
                                : "outline"
                          }
                          className={acq.paymentStatus === "Partially_Paid" ? "bg-amber-100 text-amber-800 hover:bg-amber-200" : ""}
                        >
                          {acq.paymentStatus === "Partially_Paid" ? "Partially Paid" : acq.paymentStatus}
                        </Badge>
                      </button>
                    </TableCell>
                    <TableCell>{acq.warrantyEndDate ? String(acq.warrantyEndDate).slice(0, 10) : "—"}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteAcq(acq)}>
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expandedPayments.has(acq.id) && (
                    <TableRow key={`${acq.id}-payments`}>
                      <TableCell colSpan={8} className="bg-muted/20 py-2 px-4">
                        <InfraAcquisitionPaymentsSection acq={acq} />
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* New Acquisition Dialog */}
      <Dialog open={openCreate} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Log New Acquisition</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            {/* Item picker */}
            <div className="grid gap-1.5">
              <Label>Infra Item *</Label>
              <Select value={form.infraItemId || "__none__"} onValueChange={(v) => handleItemSelect(v === "__none__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Select item…" /></SelectTrigger>
                <SelectContent>
                  {items.filter((i) => i.status === "Active").map((i) => (
                    <SelectItem key={i.id} value={String(i.id)}>{i.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Variant selector — only shown when item has variants (RESEARCH Open Q2) */}
            {form.infraItemId && itemVariants.length > 0 && (
              <div className="grid gap-1.5">
                <Label>Variant</Label>
                <Select value={form.variantId || "__none__"} onValueChange={(v) => handleVariantSelect(v === "__none__" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Select variant…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No variant</SelectItem>
                    {itemVariants.map((v) => (
                      <SelectItem key={v.id} value={String(v.id)}>
                        {v.displayName ?? `Variant #${v.id}`}
                        {v.hsnCode ? ` · HSN ${v.hsnCode}` : ""}
                        {v.gstRate != null ? ` · ${v.gstRate}% GST` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">Selecting a variant auto-fills GST rate and unit price.</p>
              </div>
            )}

            {/* Pricing model (display only, auto-filled) */}
            {form.infraItemId && (
              <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                Pricing model: <strong>{pricingModelLabel(form.pricingModel)}</strong>
              </div>
            )}

            {/* Date + Vendor */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label>Acquisition Date *</Label>
                <Input type="date" value={form.acquisitionDate} onChange={(e) => setF("acquisitionDate", e.target.value)} />
              </div>
              <div className="grid gap-1.5">
                <Label>Vendor</Label>
                <Select value={form.vendorId || "__none__"} onValueChange={(v) => setF("vendorId", v === "__none__" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {vendors.map((v) => <SelectItem key={v.id} value={String(v.id)}>{v.vendorName}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* ── Unit fields ── */}
            {form.pricingModel === "Unit" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-1.5">
                  <Label>Quantity</Label>
                  <Input type="number" min="1" value={form.qty} onChange={(e) => setF("qty", e.target.value)} placeholder="1" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Unit Price (₹)</Label>
                  <Input type="number" min="0" value={form.unitPrice} onChange={(e) => setF("unitPrice", e.target.value)} placeholder="0" />
                </div>
              </div>
            )}

            {/* ── Measurement fields ── */}
            {form.pricingModel === "Measurement" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-1.5">
                  <Label>Measurement (area / length)</Label>
                  <Input type="number" min="0" step="0.01" value={form.measurement} onChange={(e) => setF("measurement", e.target.value)} placeholder="0" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Rate per Unit (₹)</Label>
                  <Input type="number" min="0" value={form.ratePerUnit} onChange={(e) => setF("ratePerUnit", e.target.value)} placeholder="0" />
                </div>
              </div>
            )}

            {/* ── Setup / Work fields ── */}
            {form.pricingModel === "Setup_Work" && (
              <div className="grid gap-4">
                <div className="grid gap-1.5">
                  <Label>Material Cost (₹)</Label>
                  <Input type="number" min="0" value={form.materialCost} onChange={(e) => setF("materialCost", e.target.value)} placeholder="0" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Labour Basis</Label>
                  <Select value={form.labourBasis || "__none__"} onValueChange={(v) => setF("labourBasis", v === "__none__" ? "" : v as AcquisitionForm["labourBasis"])}>
                    <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Per_Day">Per Day</SelectItem>
                      <SelectItem value="Per_Area">Per Area</SelectItem>
                      <SelectItem value="Lump_Sum">Lump Sum</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.labourBasis === "Per_Day" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-1.5">
                      <Label>Labour Days</Label>
                      <Input type="number" min="0" step="0.5" value={form.labourDays} onChange={(e) => setF("labourDays", e.target.value)} placeholder="0" />
                    </div>
                    <div className="grid gap-1.5">
                      <Label>Rate / Day (₹)</Label>
                      <Input type="number" min="0" value={form.labourRate} onChange={(e) => setF("labourRate", e.target.value)} placeholder="0" />
                    </div>
                  </div>
                )}
                {form.labourBasis === "Per_Area" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-1.5">
                      <Label>Labour Area</Label>
                      <Input type="number" min="0" step="0.01" value={form.labourArea} onChange={(e) => setF("labourArea", e.target.value)} placeholder="0" />
                    </div>
                    <div className="grid gap-1.5">
                      <Label>Rate / Area (₹)</Label>
                      <Input type="number" min="0" value={form.labourRate} onChange={(e) => setF("labourRate", e.target.value)} placeholder="0" />
                    </div>
                  </div>
                )}
                {form.labourBasis === "Lump_Sum" && (
                  <div className="grid gap-1.5">
                    <Label>Labour Lump Sum (₹)</Label>
                    <Input type="number" min="0" value={form.labourLumpSum} onChange={(e) => setF("labourLumpSum", e.target.value)} placeholder="0" />
                  </div>
                )}
              </div>
            )}

            {/* Live total preview */}
            {form.infraItemId && (
              <div className="rounded-md border bg-primary/5 px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Estimated Total (client preview)</span>
                <span className="font-semibold text-lg">{inr(liveTotal)}</span>
              </div>
            )}

            {/* GST-applicable toggle + GST rate */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Switch
                  id="gst-applicable"
                  checked={form.gstApplicable}
                  onCheckedChange={(v) => {
                    setF("gstApplicable", v);
                    if (!v) setF("gstRate", "0");
                  }}
                />
                <Label htmlFor="gst-applicable">GST applicable / claimed</Label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-1.5">
                  <Label>GST Rate (%)</Label>
                  {form.gstApplicable ? (
                    <Select value={String(form.gstRate)} onValueChange={(v) => setF("gstRate", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {GST_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                      Not claimed
                    </div>
                  )}
                </div>
                <div className="grid gap-1.5">
                  <Label>Warranty Months</Label>
                  <Input type="number" min="0" value={form.warrantyMonths} onChange={(e) => setF("warrantyMonths", e.target.value)} placeholder="0" />
                </div>
              </div>
            </div>
            {form.warrantyMonths && Number(form.warrantyMonths) > 0 && (
              <div className="grid gap-1.5">
                <Label>Warranty Start Date</Label>
                <Input type="date" value={form.warrantyStartDate} onChange={(e) => setF("warrantyStartDate", e.target.value)} />
                <p className="text-xs text-muted-foreground">Warranty end date is auto-computed by the server.</p>
              </div>
            )}

            {/* Location */}
            <div className="grid gap-1.5">
              <Label>Location</Label>
              <Input value={form.location} onChange={(e) => setF("location", e.target.value)} placeholder="e.g. Server room" />
            </div>

            {/* Payment section */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Payment Details</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-1.5">
                  <Label>Paid By</Label>
                  <Select value={form.paidBy || "__none__"} onValueChange={(v) => setF("paidBy", v === "__none__" ? "" : v)}>
                    <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Not specified</SelectItem>
                      {PAID_BY.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label>Payment Method</Label>
                  <Select value={form.paymentMethod || "__none__"} onValueChange={(v) => setF("paymentMethod", v === "__none__" ? "" : v)}>
                    <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Not specified</SelectItem>
                      {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label>Account / UPI detail</Label>
                  <Input value={form.paymentAccountDetail} onChange={(e) => setF("paymentAccountDetail", e.target.value)} placeholder="Account no. / UPI id" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Reference / TXN ID</Label>
                  <Input value={form.paymentReference} onChange={(e) => setF("paymentReference", e.target.value)} placeholder="TXN12345" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Payment Date</Label>
                  <Input type="date" value={form.paymentDate} onChange={(e) => setF("paymentDate", e.target.value)} />
                </div>
                <div className="grid gap-1.5">
                  <Label>Payment Status</Label>
                  <Select value={form.paymentStatus} onValueChange={(v) => setF("paymentStatus", v as "Paid" | "Pending")}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Uploads */}
            <div className="grid gap-3">
              <Label>Invoice / Bill</Label>
              <FileField value={form.invoiceUrl} onChange={(v) => setF("invoiceUrl", v)} uploadFn={uploadInvoice} label="Invoice / bill" />
              <Label>Payment Proof</Label>
              <FileField value={form.paymentProofUrl} onChange={(v) => setF("paymentProofUrl", v)} uploadFn={uploadPaymentProof} label="Payment proof" />
            </div>

            {/* Notes */}
            <div className="grid gap-1.5">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setF("notes", e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={isBusy}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={isBusy || !form.infraItemId || !form.acquisitionDate}>
              {isBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Log Acquisition
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteAcq} onOpenChange={(o) => { if (!o) setDeleteAcq(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this acquisition?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone. The acquisition record will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteAcq && handleDelete(deleteAcq)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Warranty Tab ──────────────────────────────────────────────────────────────

function warrantyBadge(acq: InfraAcquisition) {
  if (!acq.warrantyEndDate) return null;
  const end = new Date(acq.warrantyEndDate);
  const now = new Date();
  const diffDays = Math.floor((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return <Badge variant="destructive">Expired</Badge>;
  if (diffDays <= 30) return <Badge variant="secondary" className="bg-amber-100 text-amber-800">Expiring in {diffDays}d</Badge>;
  return <Badge variant="outline">Active</Badge>;
}

function InfraWarrantyTab() {
  const [view, setView] = useState<string>("expiring_soon");
  const { data: records = [], isLoading } = useListInfraWarranty(
    view !== "__all__" ? { view: view as ListInfraWarrantyView } : {}
  );
  const { data: items = [] } = useListInfraItems();
  const itemMap = new Map(items.map((i) => [i.id, i]));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Select value={view} onValueChange={setView}>
          <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="expiring_soon">Expiring Soon (≤30 days)</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="__all__">All warranty items</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Acquisition Date</TableHead>
                <TableHead>Warranty Start</TableHead>
                <TableHead>Warranty End</TableHead>
                <TableHead>Months</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No warranty records for this view.
                  </TableCell>
                </TableRow>
              ) : records.map((acq) => (
                <TableRow key={acq.id}>
                  <TableCell className="font-medium">{itemMap.get(acq.infraItemId)?.name ?? `Item #${acq.infraItemId}`}</TableCell>
                  <TableCell>{String(acq.acquisitionDate).slice(0, 10)}</TableCell>
                  <TableCell>{acq.warrantyStartDate ? String(acq.warrantyStartDate).slice(0, 10) : "—"}</TableCell>
                  <TableCell>{acq.warrantyEndDate ? String(acq.warrantyEndDate).slice(0, 10) : "—"}</TableCell>
                  <TableCell>{acq.warrantyMonths ?? "—"}</TableCell>
                  <TableCell>{warrantyBadge(acq)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ── Categories Tab ────────────────────────────────────────────────────────────

interface CategoryForm {
  name: string;
  billingType: "Office" | "Client_Billable";
  paymentType: "Monthly_Rental" | "One_Time" | "";
  status: "Active" | "Archived";
}
const EMPTY_CATEGORY: CategoryForm = { name: "", billingType: "Office", paymentType: "", status: "Active" };

function InfraCategoriesTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: categories = [], isLoading } = useListInfraCategories();
  const createMut = useCreateInfraCategory();
  const updateMut = useUpdateInfraCategory();
  const deleteMut = useDeleteInfraCategory();

  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState<InfraCategory | null>(null);
  const [deleteRow, setDeleteRow] = useState<InfraCategory | null>(null);
  const [form, setForm] = useState<CategoryForm>(EMPTY_CATEGORY);
  const isBusy = createMut.isPending || updateMut.isPending;

  function openEdit(c: InfraCategory) {
    setForm({
      name: c.name,
      billingType: (c.billingType as "Office" | "Client_Billable") ?? "Office",
      paymentType: (c.paymentType as "Monthly_Rental" | "One_Time" | null) ?? "",
      status: (c.status as "Active" | "Archived") ?? "Active",
    });
    setEditRow(c);
    setOpen(true);
  }
  function openNew() { setForm(EMPTY_CATEGORY); setEditRow(null); setOpen(true); }
  function close() { setOpen(false); setEditRow(null); }

  async function handleSubmit() {
    if (!form.name.trim()) { toast({ title: "Name is required.", variant: "destructive" }); return; }
    const data = {
      name: form.name.trim(),
      billingType: form.billingType,
      paymentType: form.billingType === "Client_Billable" ? (form.paymentType || null) : null,
      status: form.status,
    };
    try {
      if (editRow) await updateMut.mutateAsync({ id: editRow.id, data });
      else await createMut.mutateAsync({ data });
      toast({ title: editRow ? "Category updated." : "Category added." });
      qc.invalidateQueries({ queryKey: getListInfraCategoriesQueryKey() });
      close();
    } catch (err: unknown) {
      const s = (err as { status?: number })?.status;
      toast({ title: s === 409 ? "That category already exists." : "Something went wrong.", variant: "destructive" });
    }
  }

  async function confirmDelete() {
    if (!deleteRow) return;
    try {
      await deleteMut.mutateAsync({ id: deleteRow.id });
      toast({ title: `${deleteRow.name} deleted.` });
      qc.invalidateQueries({ queryKey: getListInfraCategoriesQueryKey() });
      setDeleteRow(null);
    } catch (err: unknown) {
      const s = (err as { status?: number })?.status;
      toast({ title: s === 409 ? "Category is in use by items — reassign or archive instead." : "Could not delete.", variant: "destructive" });
      setDeleteRow(null);
    }
  }

  return (
    <div className="mt-4">
      <div className="mb-3 flex justify-end">
        <Button size="sm" onClick={openNew}><PackagePlus className="mr-2 h-4 w-4" />Add Category</Button>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Billing</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(4)].map((_, i) => <TableRow key={i}>{[...Array(5)].map((__, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>)
            ) : categories.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="h-24 text-center text-muted-foreground">No categories yet.</TableCell></TableRow>
            ) : (
              categories.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell>
                    {c.billingType === "Client_Billable"
                      ? <Badge className="bg-blue-100 text-blue-700">Client-billable</Badge>
                      : <Badge variant="secondary">Office</Badge>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {c.billingType === "Client_Billable" ? (c.paymentType ? c.paymentType.replace(/_/g, " ") : "—") : "—"}
                  </TableCell>
                  <TableCell>{c.status === "Archived" ? <Badge variant="outline" className="text-muted-foreground">Archived</Badge> : <Badge variant="secondary">Active</Badge>}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setDeleteRow(c)}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editRow ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Meeting-room AV" />
            </div>
            <div className="grid gap-1.5">
              <Label>Billing</Label>
              <Select value={form.billingType} onValueChange={(v) => setForm((f) => ({ ...f, billingType: v as "Office" | "Client_Billable" }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Office">Office-owned (internal asset)</SelectItem>
                  <SelectItem value="Client_Billable">Client-billable (client pays)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.billingType === "Client_Billable" && (
              <div className="grid gap-1.5">
                <Label>Payment type</Label>
                <Select value={form.paymentType || "unset"} onValueChange={(v) => setForm((f) => ({ ...f, paymentType: v === "unset" ? "" : v as "Monthly_Rental" | "One_Time" }))}>
                  <SelectTrigger><SelectValue placeholder="Monthly rental / One-time" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unset">— Not set —</SelectItem>
                    <SelectItem value="Monthly_Rental">Monthly rental</SelectItem>
                    <SelectItem value="One_Time">One-time</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">How a client pays when using items in this category.</p>
              </div>
            )}
            <div className="grid gap-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v as "Active" | "Archived" }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={isBusy}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={isBusy || !form.name.trim()}>{isBusy ? "Saving…" : (editRow ? "Save Changes" : "Add Category")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteRow !== null} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteRow?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This can&rsquo;t be undone. A category still used by items can&rsquo;t be deleted — reassign or archive it instead.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={confirmDelete} disabled={deleteMut.isPending}>{deleteMut.isPending ? "Deleting…" : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function InventoryInfra() {
  const [location, setLocation] = useLocation();
  const tab = tabFromPath(location);
  const [openCreate, setOpenCreate] = useState(false);

  function handleTabChange(next: string) {
    if (next === tab) return;
    setOpenCreate(false);
    setLocation(`/inventory/infra/${next}`);
  }

  return (
    <div className="space-y-4 md:space-y-6 max-w-screen-2xl mx-auto px-2 md:px-6">
      <header>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Inventory · Infra</h1>
        <p className="text-xs md:text-sm text-muted-foreground">
          Capital assets, fixtures &amp; setup works — Catalog, Acquisitions, Warranty, Categories.
        </p>
      </header>

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList className="flex w-full overflow-x-auto md:grid md:grid-cols-4 h-auto md:h-10">
          <TabsTrigger value="catalog"      className="shrink-0 whitespace-nowrap"><Building2   className="mr-2 h-4 w-4" />Catalog</TabsTrigger>
          <TabsTrigger value="acquisitions" className="shrink-0 whitespace-nowrap"><PackagePlus className="mr-2 h-4 w-4" />Acquisitions</TabsTrigger>
          <TabsTrigger value="warranty"     className="shrink-0 whitespace-nowrap"><ShieldCheck className="mr-2 h-4 w-4" />Warranty</TabsTrigger>
          <TabsTrigger value="categories"   className="shrink-0 whitespace-nowrap"><Building2   className="mr-2 h-4 w-4" />Categories</TabsTrigger>
        </TabsList>

        <TabsContent value="catalog">
          <InfraCatalogTab
            openCreate={tab === "catalog" && openCreate}
            setOpenCreate={setOpenCreate}
          />
        </TabsContent>
        <TabsContent value="acquisitions">
          <InfraAcquisitionsTab
            openCreate={tab === "acquisitions" && openCreate}
            setOpenCreate={setOpenCreate}
          />
        </TabsContent>
        <TabsContent value="warranty">
          <InfraWarrantyTab />
        </TabsContent>
        <TabsContent value="categories">
          <InfraCategoriesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
